import { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';
import GoverningBodyFormModal from '../components/GoverningBodyFormModal';
import { PencilIcon, TrashIcon, PlusIcon, ChartBarIcon } from '@heroicons/react/outline';

export default function GoverningBodies() {
  const [governingBodies, setGoverningBodies] = useState([]);
  const [filteredGoverningBodies, setFilteredGoverningBodies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoverningBody, setEditingGoverningBody] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [governingBodyToDelete, setGoverningBodyToDelete] = useState(null);
  const { t } = useLanguage();

  useEffect(() => {
    fetchGoverningBodies();
  }, []);

  useEffect(() => {
    filterGoverningBodies();
  }, [governingBodies, searchTerm, statusFilter]);

  const fetchGoverningBodies = async () => {
    try {
      const response = await api.get('/api/governing-bodies');
      setGoverningBodies(response.data);
    } catch (error) {
      console.error('Error fetching governing bodies:', error);
      toast.error(t('governingBodies.fetchError'));
    } finally {
      setLoading(false);
    }
  };

  const filterGoverningBodies = () => {
    let filtered = governingBodies;

    if (searchTerm) {
      filtered = filtered.filter(gb =>
        gb.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        gb.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(gb => 
        statusFilter === 'active' ? gb.isActive : !gb.isActive
      );
    }

    setFilteredGoverningBodies(filtered);
  };

  const handleSubmit = async (formData) => {
    try {
      if (editingGoverningBody) {
        await api.put(`/api/governing-bodies/${editingGoverningBody.id}`, formData);
        toast.success(t('governingBodies.updateSuccess'));
      } else {
        await api.post('/api/governing-bodies', formData);
        toast.success(t('governingBodies.createSuccess'));
      }
      setIsModalOpen(false);
      setEditingGoverningBody(null);
      fetchGoverningBodies();
    } catch (error) {
      console.error('Error saving governing body:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(editingGoverningBody ? t('governingBodies.updateError') : t('governingBodies.createError'));
      }
    }
  };

  const handleEdit = (governingBody) => {
    setEditingGoverningBody(governingBody);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/api/governing-bodies/${governingBodyToDelete.id}`);
      toast.success(t('governingBodies.deleteSuccess'));
      setDeleteConfirmOpen(false);
      setGoverningBodyToDelete(null);
      fetchGoverningBodies();
    } catch (error) {
      console.error('Error deleting governing body:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('governingBodies.deleteError'));
      }
    }
  };

  const openDeleteConfirm = (governingBody) => {
    setGoverningBodyToDelete(governingBody);
    setDeleteConfirmOpen(true);
  };

  const handleViewStats = (governingBody) => {
    // Navigate to governing body stats page or show stats modal
    window.location.href = `/governing-bodies/${governingBody.id}/stats`;
  };

  if (loading) {
    return <div className="py-6 px-4 sm:px-6 lg:px-8">{t('common.loading')}</div>;
  }

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">{t('nav.governingBodies')}</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center"
        >
          <PlusIcon className="mr-2 h-5 w-5" />
          {t('governingBodies.addGoverningBody')}
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <input
            type="text"
            placeholder={t('governingBodies.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          >
            <option value="all">{t('governingBodies.allStatuses')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>
        </div>
      </div>

      {filteredGoverningBodies.length === 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6 text-center text-gray-500">
          {t('governingBodies.noGoverningBodies')}
        </div>
      ) : (
        <div className="mt-8 flex flex-col">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('governingBodies.name')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('governingBodies.email')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('governingBodies.phone')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('governingBodies.companies')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('governingBodies.children')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.status')}
                    </th>
                    <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredGoverningBodies.map((governingBody) => (
                    <tr key={governingBody.id}>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <div>
                          <div className="font-medium text-gray-900">{governingBody.name}</div>
                          {governingBody.parent && (
                            <div className="text-sm text-gray-500">
                              {t('governingBodies.parentOf', { parent: governingBody.parent.name })}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {governingBody.email}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {governingBody.phone || '-'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div>
                          <span className="text-sm font-medium">{governingBody.companies?.length || 0}</span>
                          {governingBody.companies?.length > 0 && (
                            <div className="text-xs text-gray-500 mt-1">
                              {governingBody.companies.slice(0, 2).map(company => company.name).join(', ')}
                              {governingBody.companies.length > 2 && ` +${governingBody.companies.length - 2} more`}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        {governingBody.children?.length || 0}
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm">
                        <span
                          className={`inline-flex rounded-full px-2 text-xs font-semibold leading-5 ${
                            governingBody.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {governingBody.isActive ? t('common.active') : t('common.inactive')}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => handleViewStats(governingBody)}
                            className="text-blue-600 hover:text-blue-900"
                            title={t('governingBodies.viewStats')}
                          >
                            <ChartBarIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => handleEdit(governingBody)}
                            className="text-indigo-600 hover:text-indigo-900"
                            title={t('common.edit')}
                          >
                            <PencilIcon className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => openDeleteConfirm(governingBody)}
                            className="text-red-600 hover:text-red-900"
                            title={t('common.delete')}
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Governing Body Form Modal */}
      <GoverningBodyFormModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingGoverningBody(null);
        }}
        onSubmit={handleSubmit}
        initialValues={editingGoverningBody}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <TrashIcon className="h-6 w-6 text-red-600" aria-hidden="true" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {t('governingBodies.deleteConfirmTitle')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        {t('governingBodies.deleteConfirmMessage', { 
                          name: governingBodyToDelete?.name 
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleDelete}
                >
                  {t('common.delete')}
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setDeleteConfirmOpen(false)}
                >
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 